import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { CommonApiService } from '../../proxy/services/common-api.service';
import { ExecutionStatus } from '../../proxy/models/common';
import { LoadingService } from '../services/loading.service';
import { IndexDbService } from '../services/indexDb.service';
import { AllLookupsService } from '../services/all-lookups.service';
import { GlobalConfigsService } from '../services/global-configs.service';
import { IndexDbKeys, SystemCheckSums } from '../../proxy/models/index-db-keys';

export const dataGuard: CanActivateFn = (): Observable<boolean> => {
    const apiService = inject(CommonApiService);
    const loaderService = inject(LoadingService);
    const indexDbService = inject(IndexDbService);
    const lookupsService = inject(AllLookupsService);
    const configsService = inject(GlobalConfigsService);

    loaderService.showLoader();

    function getCheckSumByKey(systemCheckSums: SystemCheckSums[], key: any) {
        // @ts-ignore
        return systemCheckSums.find(el => el.Key === IndexDbKeys[key]);
    }

    return apiService.getSystemCheckSums().pipe(
        switchMap(res => {
            if (res.result.status !== ExecutionStatus.success) {
                loaderService.hideLoader();
                return of(false);
            }

            const summary = {
                LookupChecksum: getCheckSumByKey(res.systemCheckSums, IndexDbKeys.LookupChecksum),
                ConfigChecksum: getCheckSumByKey(res.systemCheckSums, IndexDbKeys.ConfigChecksum),
            } as any;

            return forkJoin([
                indexDbService.getByKey(summary.LookupChecksum.Key),
                indexDbService.getByKey(summary.ConfigChecksum.Key),
            ]).pipe(
                switchMap((allData: any[]) => {
                    const updateRequests: Observable<any>[] = [];

                    allData.forEach((data, index) => {
                        const checksumKey = index === 0 ? IndexDbKeys.LookupChecksum : IndexDbKeys.ConfigChecksum;
                        if (data && data?.checkSumValue === summary[checksumKey]?.Value) {
                            if (checksumKey === IndexDbKeys.LookupChecksum) {
                                lookupsService.setLookups({ ...data, checkSumValue: summary[data.key]?.Value });
                            } else if (checksumKey === IndexDbKeys.ConfigChecksum) {
                                configsService.setConfigs({ ...data, checkSumValue: summary[data.key]?.Value });
                            }
                        } else if (!data || (data.checkSumValue !== summary[checksumKey]?.Value)) {
                            if (checksumKey === IndexDbKeys.LookupChecksum) {
                                updateRequests.push(
                                    lookupsService.getAllLookups().pipe(
                                        switchMap(response => {
                                            const data = JSON.parse(response.records[0]?.LookupTableJson)?.LookupTables;
                                            return lookupsService.setLookups({
                                                value: data,
                                                checkSumValue: summary[checksumKey]?.Value,
                                            }).pipe(switchMap(() => of(true)));
                                        })
                                    )
                                );
                            } else if (checksumKey === IndexDbKeys.ConfigChecksum) {
                                updateRequests.push(
                                    configsService.getAllConfigs()?.pipe(
                                        switchMap((response: any) =>
                                            configsService.setConfigs({
                                                value: response?.abeList,
                                                checkSumValue: summary[checksumKey]?.Value,
                                            }).pipe(switchMap(() => of(true)))
                                        )
                                    )
                                );
                            }
                            loaderService.showLoader();
                        } else if (data) {
                            if (checksumKey === IndexDbKeys.LookupChecksum) {
                                lookupsService.setLookups({ ...data, checkSumValue: summary[data.key]?.Value });
                            } else if (checksumKey === IndexDbKeys.ConfigChecksum) {
                                configsService.setConfigs({ ...data, checkSumValue: summary[data.key]?.Value });
                            }
                        }
                    });

                    if (updateRequests.length > 0) {
                        return forkJoin(updateRequests).pipe(
                            map(() => {
                                loaderService.hideLoader();
                                return true;
                            })
                        );
                    } else {
                        loaderService.hideLoader();
                        return of(true);
                    }
                })
            );
        }),
        catchError(() => {
            loaderService.hideLoader();
            return of(false);
        })
    );
};
